import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

const PHASES = [
  {
    phase: "Phase 1",
    title: "Foundation",
    status: { label: "Live", cls: "border-emerald-400/30 bg-emerald-400/10 text-emerald-400", dot: "bg-emerald-400" },
    items: [
      "Winter Protocol application",
      "AI-powered ecosystem",
      "WIN Token ecosystem",
      "Rewards & referral infrastructure",
    ],
  },
  {
    phase: "Phase 2",
    title: "Blockchain & Exchange",
    status: { label: "In Development", cls: "border-amber-400/30 bg-amber-400/10 text-amber-400", dot: "bg-amber-400" },
    items: [
      "Next-generation blockchain infrastructure",
      "Greater transparency",
      "WIN Exchange development",
      "Expanded ecosystem infrastructure",
    ],
  },
  {
    phase: "Phase 3",
    title: "Digital Financial Ecosystem",
    status: { label: "Future", cls: "border-gray-500/30 bg-gray-500/10 text-gray-400", dot: "bg-gray-500" },
    items: [
      "Winter Pay",
      "Low-cost money transfers",
      "Trader Marketplace",
      "Market Insights & Signals",
    ],
  },
  {
    phase: "Phase 4",
    title: "Global Expansion",
    status: { label: "Future", cls: "border-gray-500/30 bg-gray-500/10 text-gray-400", dot: "bg-gray-500" },
    items: [
      "Global expansion",
      "Advanced AI financial tools",
      "Expanded WIN Token ecosystem",
      "Broader digital financial infrastructure",
    ],
  },
];

export default function Roadmap() {
  const [open, setOpen] = useState(0);

  return (
    <section id="roadmap" className="relative py-24 sm:py-32 border-t border-white/[0.06]" data-testid="roadmap-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_80%_10%,rgba(212,175,55,0.05),transparent_55%)]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          chapter="Chapter 07"
          kicker="Execution Timeline"
          title="The Road Ahead."
          subtitle="A phased path from foundation to a global digital financial ecosystem."
        />

        <div className="relative mt-16">
          <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-[#D4AF37]/50 via-white/10 to-transparent sm:left-1/2" aria-hidden="true" />

          <div className="space-y-10">
            {PHASES.map((p, i) => (
              <Reveal key={p.phase} delay={i * 0.06}>
                <div className={`relative flex ${i % 2 === 0 ? "sm:justify-start" : "sm:justify-end"}`}>
                  <span
                    className="absolute left-[19px] top-8 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-[#D4AF37] bg-[#08080A] sm:left-1/2"
                    aria-hidden="true"
                  />
                  <div className={`ml-12 w-full sm:ml-0 sm:w-[calc(50%-3rem)] ${i % 2 === 0 ? "" : ""}`}>
                    <button
                      onClick={() => setOpen(open === i ? -1 : i)}
                      data-testid={`roadmap-phase-${i + 1}-card`}
                      className={`w-full text-left glass-card p-7 transition-all duration-500 hover:border-[#D4AF37]/30 ${open === i ? "border-[#D4AF37]/40" : ""}`}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="font-mono text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase">{p.phase}</p>
                          <h3 className="mt-2 font-display text-xl sm:text-2xl font-bold text-white">{p.title}</h3>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`hidden sm:inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] tracking-widest uppercase ${p.status.cls}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${p.status.dot}`} />
                            {p.status.label}
                          </span>
                          <ChevronDown
                            className={`h-5 w-5 text-[#636B78] transition-transform duration-500 ${open === i ? "rotate-180 text-[#D4AF37]" : ""}`}
                          />
                        </div>
                      </div>

                      <AnimatePresence initial={false}>
                        {open === i && (
                          <motion.ul
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            className="overflow-hidden"
                          >
                            <div className="mt-5 space-y-3 border-t border-white/[0.07] pt-5">
                              {p.items.map((item) => (
                                <li key={item} className="flex items-center gap-3 text-sm text-[#99A1AF] list-none">
                                  <span className="h-1 w-1 rotate-45 bg-[#D4AF37]" aria-hidden="true" />
                                  {item}
                                </li>
                              ))}
                            </div>
                          </motion.ul>
                        )}
                      </AnimatePresence>
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-14 text-center font-mono text-[11px] tracking-[0.18em] text-[#636B78] uppercase">
            Roadmap represents the planned development direction and may evolve over time.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
